import React, {useState, useEffect} from 'react';

import "./ChangeIdeaOverview.css";
import {StandardButton} from "../../../../common/button/standard-button/StandardButton";
import {BreadCrumbTrail} from "../../../../common/breadcrumb-trail/BreadcrumbTrail";
import rootCauseIcon from "../../../../../resources/images/root-cause-icon.png";
import {OverviewListItem} from "../../../../common/list/overview-list-item/OverviewListItem";
import {deleteChangeIdea, viewChangeIdea, viewChangeIdeasForCause} from "../../../../../services/ChangeIdeaService";
import {ToolTip} from "../../../../common/tool-tip/ToolTip";
import {ConfirmDeleteModal} from "../../../../common/confirmation-modal/ConfimDeleteModal";

export const ChangeIdeaOverview = ({context, setContext, navigateTo, setSpinnerText, showToaster, toolTipsEnabled}) => {

    const [changeIdeaList, setChangeIdeaList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [extRefToBeDeleted, setExtRefToBeDeleted] = useState(null);
    const [modalTitle, setModalTitle] = useState(null);

    useEffect(() => {
        if (!context.viewMode) {
            setContext((prevState) => ({
                ...prevState,
                breadcrumbs: {
                    ...prevState.breadcrumbs,
                    changeIdeaBreadcrumb: {
                        text: "Overview",
                        type: "Change Idea",
                        screenKey: "changeIdeaOverview",
                    },
                },
            }));
        }

        const fetchData = async () => {
            setSpinnerText("Retrieving Change Ideas");
            await retrieveChangeIdeas();
            setSpinnerText(null);
        }
        fetchData();
    }, []);

    const retrieveChangeIdeas = async () => {
        const [data, error] = await viewChangeIdeasForCause(context.username, context.rootCauseScreen.externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const mappedChangeIdeaList = data.changeIdeaVOList.map((changeIdea) => {

                    let toImprove = null;
                    let from = null;
                    let to = null;
                    let byDate = null;

                    if (changeIdea.aimStatementVO) {
                        toImprove = changeIdea.aimStatementVO.toImprove;
                        from = changeIdea.aimStatementVO.from;
                        to = changeIdea.aimStatementVO.to;
                        byDate = changeIdea.aimStatementVO.by;
                    }

                    return {
                        externalReference: changeIdea.externalReference,
                        changeConcept: changeIdea.concept,
                        priority: changeIdea.priority,
                        description: changeIdea.description,
                        prediction: changeIdea.prediction,
                        toImprove,
                        from,
                        to,
                        byDate,
                        processMeasureList: changeIdea.processMeasureVOList,
                        title: changeIdea.title,
                    }
                });
                setChangeIdeaList(mappedChangeIdeaList);
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
    }

    const handleAddChangeIdeaClick = () => {
        setContext((prevState) => ({
            ...prevState,
            changeIdeaScreen: {
                visitExternalReference: context.topicAreaScreen.visitExternalReference,
                topicAreaExternalReference: context.topicAreaScreen.topicArea.externalReference,
                causeExternalReference: context.rootCauseScreen.externalReference,
            },
            breadcrumbs: {
                ...prevState.breadcrumbs,
                testBreadCrumb: null,
            },
        }));
        navigateTo("changeIdeaOne");
    }

    const handleListItemClick = (changeIdea) => {
        setContext((prevState) => ({
            ...prevState,
            changeIdeaScreen: {
                ...changeIdea,
                priority: {value: changeIdea.priority, label: changeIdea.priority},
            }
        }));
        navigateTo("PDSAOverview");
    }

    const handleViewListItemClick = async (changeIdeaExternalReference) => {
        const savedChangeIdea = await fetchChangeIdea(changeIdeaExternalReference);

        let toImprove = null;
        let from = null;
        let to = null;
        let byDate = null;

        if (savedChangeIdea.aimStatementVO) {
            toImprove = savedChangeIdea.aimStatementVO.toImprove;
            from = savedChangeIdea.aimStatementVO.from;
            to = savedChangeIdea.aimStatementVO.to;
            byDate = savedChangeIdea.aimStatementVO.by;
        }

        setContext((prevState) => ({
            ...prevState,
            copyOfChangeIdeaScreen: null,
            changeIdeaScreen: {
                changeConcept: savedChangeIdea.concept,
                priority: {label: savedChangeIdea.priority, value: savedChangeIdea.priority,},
                description: savedChangeIdea.description,
                prediction: savedChangeIdea.prediction,
                toImprove,
                from,
                to,
                byDate,
                processMeasureList: savedChangeIdea.processMeasureVOList,
                PDSAList: [],
                externalReference: savedChangeIdea.externalReference,
                causeExternalReference: savedChangeIdea.causeExternalReference,
                visitExternalReference: savedChangeIdea.visitExternalReference,
                topicAreaExternalReference: savedChangeIdea.topicAreaExternalReference,
                title: savedChangeIdea.title,
                changeIdeaFileList: savedChangeIdea.changeIdeaFileList,
            }
        }));
        navigateTo("changeIdeaOne");
    }

    const fetchChangeIdea = async (changeIdeaExternalReference) => {
        let savedChangeIdea = null;
        const [data, error] = await viewChangeIdea(context.username, changeIdeaExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                savedChangeIdea = data.changeIdeaVOToView;
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        return savedChangeIdea;
    }

    const handleDeleteListItemClick = async (externalReference) => {
        setSpinnerText("Deleting Change Idea");
        let changeIdeaDeleted = false;
        const [data, error] = await deleteChangeIdea(context.username, externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                changeIdeaDeleted = true;
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }

        if (changeIdeaDeleted) {
            await retrieveChangeIdeas();
            setShowModal(false);
        }
        setSpinnerText(null);
    }

    const buildChangeIdealList = () => {
        if (changeIdeaList) {
            return changeIdeaList.map((changeIdea, index) => {
                return (
                    <OverviewListItem key={index}
                                      disableDelete={!context.editMode}
                                      text={changeIdea.title}
                                      buttonThreeText={"PDSA"}
                                      onItemClick={() => {
                                          handleListItemClick(changeIdea);
                                      }}
                                      onViewClick={() => {
                                          handleViewListItemClick(changeIdea.externalReference);
                                      }}
                                      onDeleteClick={() => {
                                          handleDeleteModalShow(changeIdea.externalReference, changeIdea.title);
                                      }}/>
                );
            });
        }
    }

    const handleDeleteModalShow = async (extRef, title) => {
        setShowModal(true);
        setExtRefToBeDeleted(extRef);
        setModalTitle(title);
    }

    return (
        <div className="wizard-content-container">
            <div className="wizard-main-content-container">
                <div className="wizard-left-container">
                    <div className="wizard-icon-container">
                        <img src={rootCauseIcon} alt="Root Cause Icon" className="wizard-icon"/>
                    </div>
                    <p className="wizard-icon-label">{context.rootCauseScreen.name}</p>
                </div>
                <div className="wizard-right-container">
                    <div className="change-idea-overview-list-container">
                        <div className={"label-div-tooltip-h2"}>
                            <label className={"label-h2"}>Change Idea Overview</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"cioverview123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Change Idea Overview:"}/>}
                        </div>
                        {buildChangeIdealList()}
                        <StandardButton text="Add Change Idea" onClick={handleAddChangeIdeaClick} disabled={!context.editMode}/>
                    </div>
                    {context.viewMode && <div className="back-to-overview-button-container">
                        <StandardButton text="Problem Overview" onClick={() => {
                            navigateTo("problemTree");
                        }}/>
                    </div>}
                    <div className="change-idea-overview-button-container">
                        <StandardButton text="Root Cause Overview" onClick={() => {
                            navigateTo("rootCauseOverview");
                        }}/>
                    </div>
                </div>
            </div>
            {showModal &&
            <ConfirmDeleteModal showModal={showModal}
                                setShowModal={setShowModal}
                                titleToDelete={modalTitle}
                                onDeleteClick={() => {
                                    handleDeleteListItemClick(extRefToBeDeleted)
                                }}/>}
            <div className="wizard-breadcrumb-container">
                <BreadCrumbTrail context={context} navigateTo={navigateTo}/>
            </div>
        </div>
    );
}
